import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useToast } from '../components/ui/use-toast';
import { apiRequest } from '../config/api';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const { toast } = useToast();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const token = localStorage.getItem('session_token');
      if (token) {
        try {
          // Double-check token is still the same (in case it was cleared during async operation)
          const currentToken = localStorage.getItem('session_token');
          if (currentToken !== token) {
            console.log('Session check: Token changed during verification, aborting');
            setUser(null);
            setProfile(null);
            setLoading(false);
            return;
          }
          
          const data = await apiRequest('verify_session.php');
          
          // Check token again after API call
          const tokenAfterCall = localStorage.getItem('session_token');
          if (tokenAfterCall !== token) {
            console.log('Session check: Token changed after API call, aborting');
            setUser(null);
            setProfile(null);
            setLoading(false);
            return;
          }
          
          if (data.success && data.user) {
            // Ensure role is set correctly - prioritize explicit role from API
            let userRole = data.user.role;
            
            // If role is missing, try to infer from user data
            if (!userRole) {
              // If it has patient_unique_id, it's definitely a patient
              if (data.user.patient_unique_id) {
                userRole = 'patient';
                console.log('Session check: Inferred role as patient from patient_unique_id');
              }
              // If it has specialization or clinic_name, it's likely a doctor/admin
              else if (data.user.specialization || data.user.clinic_name) {
                // Check the actual role from the database response
                userRole = data.user.role || 'doctor';
                console.log('Session check: Inferred role as', userRole, 'from specialization/clinic_name');
              }
              // If role is explicitly set in the response, use it
              else if (data.user.role) {
                userRole = data.user.role;
                console.log('Session check: Using role from API response:', userRole);
              }
            }
            
            // Final fallback - if still no role, log warning and try to infer
            if (!userRole) {
              console.warn('Session check: Could not determine role, user data:', {
                id: data.user.id,
                has_patient_unique_id: !!data.user.patient_unique_id,
                has_specialization: !!data.user.specialization,
                has_clinic_name: !!data.user.clinic_name,
                raw_role: data.user.role
              });
              // Last resort: check if it's a patient by patient_unique_id
              userRole = data.user.patient_unique_id ? 'patient' : 'doctor';
            }
            
            const userData = {
              ...data.user,
              role: userRole
            };
            
            // Normalize role to lowercase for consistency
            if (userData.role) {
              userData.role = userData.role.toLowerCase().trim();
            }
            if (userRole) {
              userRole = userRole.toLowerCase().trim();
            }
            
            console.log('Session verified - User data:', {
              id: userData.id,
              role: userRole,
              normalized_role: userData.role,
              name: userData.full_name,
              has_patient_unique_id: !!userData.patient_unique_id,
              has_specialization: !!userData.specialization,
              raw_role_from_api: data.user.role,
              current_path: window.location.pathname
            });
            
            // Double-check: If we're on a patient route and role is not patient, log warning
            if (window.location.pathname.startsWith('/patient/') && userRole !== 'patient') {
              console.error('Session verification WARNING: On patient route but role is', userRole, 'User data:', userData);
            }
            
            // Double-check: If we're on a doctor route and role is patient, log warning
            if ((window.location.pathname.startsWith('/doctor/') || window.location.pathname.startsWith('/admin/')) && userRole === 'patient') {
              console.error('Session verification WARNING: On doctor/admin route but role is patient. User data:', userData);
            }
            
            // Ensure role is set correctly
            if (!userData.role) {
              console.warn('Session verification: Role is missing, inferring from context');
              if (userData.patient_unique_id) {
                userData.role = 'patient';
              } else if (userData.specialization || userData.clinic_name) {
                userData.role = 'doctor';
              } else {
                userData.role = userRole || 'doctor';
              }
            }
            
            setUser(userData);
            setProfile(userData);
          } else {
            console.log('Session verification failed:', data);
            localStorage.removeItem('session_token');
            setUser(null);
            setProfile(null);
          }
        } catch (error) {
          console.error('Session verification error:', error);
          localStorage.removeItem('session_token');
          setUser(null);
          setProfile(null);
        }
      } else {
        // No token, ensure user and profile are null
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    };

    checkSession();
  }, []);

  const signUp = useCallback(async (email, password, userData) => {
    try {
      const data = await apiRequest('register.php', {
        method: 'POST',
        body: JSON.stringify({
          name: userData.name,
          email: email,
          password: password,
          phone: userData.phone,
          role: userData.role,
          clinic_name: userData.clinic_name,
        }),
      });

      if (data.success) {
        return { user: data.user, error: null };
      } else {
        throw new Error(data.error || 'Registration failed');
      }
    } catch (error) {
      return { user: null, error };
    }
  }, []);

  const signIn = useCallback(async (username, password) => {
    try {
      // Clear any existing session token and state before logging in to prevent conflicts
      const oldToken = localStorage.getItem('session_token');
      if (oldToken) {
        console.log('Clearing old session token before login');
        localStorage.removeItem('session_token');
      }
      
      // Clear user state immediately to prevent race conditions
      setUser(null);
      setProfile(null);
      
      const data = await apiRequest('login.php', {
        method: 'POST',
        body: JSON.stringify({
          username: username,
          password: password,
        }),
      });

      if (data.success) {
        // Store new session token
        localStorage.setItem('session_token', data.session_token);
        
        // Ensure role is set correctly - prioritize explicit role from API
        let userRole = data.user.role;
        
        // If role is missing, try to infer it
        if (!userRole) {
          if (data.user.patient_unique_id) {
            userRole = 'patient';
          } else if (data.user.specialization || data.user.clinic_name) {
            // Has doctor/admin characteristics
            userRole = data.user.role || 'doctor';
          }
        }
        
        // Ensure role is always set
        if (!userRole) {
          console.warn('Login: Role not found in response, defaulting based on user data');
          userRole = data.user.patient_unique_id ? 'patient' : 'doctor';
        }
        
        const userData = {
          ...data.user,
          role: userRole
        };
        
        console.log('Login successful - User data:', {
          id: userData.id,
          role: userRole,
          name: userData.full_name,
          has_patient_unique_id: !!userData.patient_unique_id,
          has_specialization: !!userData.specialization,
          raw_role_from_api: data.user.role,
          session_token: data.session_token.substring(0, 10) + '...'
        });
        
        setUser(userData);
        setProfile(userData);
        return { error: null };
      } else {
        const errorMessage = data.message || data.error || 'Login failed';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      // Ensure state is cleared on error
      localStorage.removeItem('session_token');
      setUser(null);
      setProfile(null);
      return { error };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      // Clear state immediately to prevent race conditions
      setUser(null);
      setProfile(null);
      
      // Remove token from localStorage immediately
      localStorage.removeItem('session_token');
      
      // Then call logout API (don't wait for it)
      apiRequest('logout.php', {
        method: 'POST',
      }).catch(error => {
        console.error('Logout API error (non-critical):', error);
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Ensure state is cleared even if API call fails
      localStorage.removeItem('session_token');
      setUser(null);
      setProfile(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      signUp,
      signIn,
      signOut,
    }),
    [user, profile, loading, signUp, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

